import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ButtonHLPure, ClassificationPanelPure, DialogSystemGLPure } from '../../pures'
import { NIL as NIL_UUID } from 'uuid'
import { useTranslation } from 'react-i18next'
import './questionnaires-assign-categories.css'

const QuestionnnairesAssignCategories = (props) => {
  const {
    userCompanies,
    subCategories,
    typeDocuments,
    handleAssignCategories,
    selectedQuestionnaire,
    hideShowDetails,
    getUserCategories,
    setDigitalVaultCategory,
    refreshCategories,
    permission
  } = props

  const { t } = useTranslation();

  const treeRef = useRef(null)
  const [select, setSelect] = useState([''])
  const [companyId, setCompanyId] = useState(NIL_UUID)
  const [categoryId, setCategoryId] = useState(0)
  const [typeDocument, setTypeDocument] = useState('')
  const [showErrorCompany, setShowErrorCompany] = useState(false)
  const [showErrorTypeDoc, setShowTypeDoc] = useState(false)
  const [showErrorCategories, setShowErrorCategories] = useState(false)
  const [showErrorSub, setShowErrorSub] = useState(false)
  const [expand, setExpand] = useState({
    ids: [],
    idField: 'subcategory.id',
  })

  const [expandedNodes, setExpandedNodes] = useState([])
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(0)

  // Copia accesible de categorías (para cálculos de índice)
  const [filteredCategories, setFilteredCategories] = useState([])

  // Estados para crear subcategorías
  const [showCategoryDialog, setShowCategoryDialog] = useState(false)
  const [categoryName, setCategoryName] = useState('')
  const [categoryNameError, setCategoryNameError] = useState(false)

  // Estados para modales (igual que signature-profiles)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [textError, setTextError] = useState('')

  // Control para evitar múltiples cargas iniciales
  const initialLoadDone = useRef(false)
  const dataLoadedRef = useRef(false)

  useEffect(() => {
    if (dataLoadedRef.current) return
    dataLoadedRef.current = true

    if (props.getUserCompanies && userCompanies.length <= 1) {
      props.getUserCompanies()
    }
    if (props.getTypeDocuments && typeDocuments.length === 0) {
      props.getTypeDocuments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setFilteredCategories(subCategories)
  }, [subCategories])

  const canCreateSubcategory = useMemo(() => {

    if (!permission || !permission.subcategory) {
      return false
    }

    const result = permission.subcategory.create === true
    return result
  }, [permission])

  // Función para abrir el diálogo de crear subcategoría
  const handleAddSubcategory = () => {
    setShowCategoryDialog(true)
  }

  // Función para crear la subcategoría (EXACTAMENTE igual que signature-profiles)
  const handleCreateSubcategory = async () => {
    if (!categoryName.trim()) {
      setCategoryNameError(true)
      return
    }
    setCategoryNameError(false)

    // 1. PRIMERO cerrar el diálogo
    setShowCategoryDialog(false)
    setCategoryName('')

    // 2. LUEGO mostrar loading
    setIsLoading(true)

    try {
      // Usar selectedSubCategoryId como padre (igual que signature-profiles)
      const parentId = selectedSubCategoryId > 0 ? selectedSubCategoryId : null

      const response = await props.createSubcategory({
        categoryName: categoryName.trim(),
        companyId,
        parentId
      })

      if (response && response.id) {
        // Refrescar el árbol IGUAL que signature-profiles-details
        await refreshCategories(companyId)

        // Mostrar success y ocultar después de 2 segundos
        setIsLoading(false)
        setSuccess(true)
        setTimeout(() => setSuccess(false), 2000)
      } else {
        throw new Error('Error al crear subcategoría')
      }
    } catch (error) {
      console.error(error)
      setIsLoading(false)
      setError(true)
      setTextError(error.message || 'Error al crear subcategoría')
      setTimeout(() => setError(false), 3000)
    }
  }

  // ELIMINADO: useEffect duplicado que causaba re-renders innecesarios
  // setFilteredCategories ya se hace en el primer useEffect (línea 53)

  // NOTA: La inicialización de empresa, tipo documento, categorías, etc.
  // se maneja completamente en el useEffect principal (línea 268)
  // para evitar múltiples cargas y saltos en el árbol


  // Cambio de campos (empresa / tipo doc)
  const handleChange = (e, setValue, type, setShowError, typeSelect) => {
    let value = e.target.value
    if (type === 'Number') value = Number(value)
    if (setShowError) setShowError(false)
    setValue(value)

    if (typeSelect === 'company') {
      getUserCategories(value, setCategoryId, setSelect)
    }
  }

  // Evento de selección del árbol
  const onChangeTree = (event) => {
    const node = event?.nodeData
    if (!node) return

    const nodeId = node.subcategory?.id ?? node.id
    const parentId = node.parentID || node.parentId || 0

    // Intentar obtener el index jerárquico
    let indexPath = node.index
    if (!indexPath && treeRef.current) {
      try {
        const parents = []
        let currentId = nodeId
        let current = filteredCategories.find(
          (n) => String(n.id) === String(currentId)
        )

        while (current && current.parentId) {
          const siblings = filteredCategories.filter(
            (n) => String(n.parentId) === String(current.parentId)
          )
          const idx = siblings.findIndex(
            (n) => String(n.id) === String(current.id)
          )
          parents.unshift(idx)
          current = filteredCategories.find(
            (n) => String(n.id) === String(current.parentId)
          )
        }

        indexPath = parents.join('_')
      } catch (err) {
        console.log(err)
      }
    }

    // Para que aparezca el botón "+ Agregar subcategoría" en ClassificationPanelPure,
    // necesitamos que categoryId sea el CONTENEDOR donde se creará la subcategoría.
    // - Si el nodo seleccionado es una categoría (parentId = 0), entonces categoryId debe ser el id del nodo.
    // - Si el nodo seleccionado es una subcategoría, entonces categoryId debe ser su parentId.
    const containerCategoryId = (!parentId || Number(parentId) === 0) ? nodeId : parentId
    setCategoryId(containerCategoryId)
    // Guardamos el nodo seleccionado para usarlo como referencia de creación/selección
    setSelectedSubCategoryId(nodeId)
    setSelect([String(nodeId)])
    setShowErrorSub(false)
  }

  const buildHierarchicalIndex = (categories, targetId) => {
    const targetNode = categories.find(cat => cat.id === targetId)
    if (!targetNode) return ""
    const path = []
    let currentNode = targetNode
    while (currentNode) {
      const siblings = categories.filter(cat => cat.parentId === currentNode.parentId)
      const index = siblings.findIndex(cat => cat.id === currentNode.id)
      path.unshift(index)
      if (!currentNode.parentId || currentNode.parentId === 0) break
      currentNode = categories.find(cat => cat.id === currentNode.parentId)
    }
    return path.join('_')
  }

  const getParentIds = (categories, targetId) => {
    const parentIds = []
    let currentNode = categories.find(cat => String(cat.id) === String(targetId))
    while (currentNode && currentNode.parentId && currentNode.parentId !== 0) {
      parentIds.push(currentNode.parentId)
      currentNode = categories.find(cat => String(cat.id) === String(currentNode.parentId))
    }

    return parentIds
  }

  const getCategoriesAndSelectSubcategory = async (companyId, subCategoryId, pathBreadcrumb) => {
    if (!companyId || companyId === NIL_UUID) return

    try {
      const response = await getUserCategories(companyId, setCategoryId, setSelect)

      if (response && response.data && response.data.fileData) {
        const categories = response.data.fileData
        setFilteredCategories(categories)

        if (!subCategoryId || categories.length === 0) return

        const selectedNode = categories.find(cat => String(cat.id) === String(subCategoryId))
        if (!selectedNode) return

        const hierarchicalIndex = buildHierarchicalIndex(categories, subCategoryId)
        const parentIds = getParentIds(categories, subCategoryId)

        setSelectedSubCategoryId(subCategoryId)
        setCategoryId(parentIds[parentIds.length - 1] || 0)
        setExpandedNodes(parentIds.map(id => id.toString()))
        setSelect([hierarchicalIndex])

        // UNA SOLA VEZ: scroll al nodo después de que se renderice
        setTimeout(() => {
          if (!treeRef.current) return
          try {
            const tree = treeRef.current
            const liElements = tree.element.querySelectorAll('li[data-uid]')
            let targetUid = null
            liElements.forEach(li => {
              const nodeData = tree.getNode(li)
              if (nodeData && nodeData.id && nodeData.id.toString() === subCategoryId.toString()) {
                targetUid = li.getAttribute('data-uid')
              }
            })
            if (targetUid) {
              tree.selectedNodes = [targetUid]
              tree.ensureVisible(targetUid)
            }
          } catch (err) {
            console.log(err)
          }
        }, 300)
      }
    } catch (err) {
      console.log(err)
    }
  }

  // OPTIMIZADO: Solo se ejecuta UNA VEZ al montar o cuando cambia selectedQuestionnaire
  useEffect(() => {
    // Resetear el flag cuando cambia el cuestionario seleccionado
    initialLoadDone.current = false

    if (!selectedQuestionnaire || !selectedQuestionnaire.classifier) return

    // Establecer valores iniciales INMEDIATAMENTE (sin esperar userCompanies)
    const { companyId, categoryId, subCategoryId, pathBreadcrumb } = selectedQuestionnaire.classifier

    if (companyId && companyId !== NIL_UUID) {
      setCompanyId(companyId)
      setCategoryId(categoryId || 0)
      setSelectedSubCategoryId(subCategoryId || 0)
    }

    if (userCompanies.length <= 1) return

    // Evitar múltiples cargas de categorías
    if (initialLoadDone.current) return
    initialLoadDone.current = true

    if (companyId && companyId !== NIL_UUID) {
      // Llamar una sola vez para cargar y seleccionar
      getCategoriesAndSelectSubcategory(companyId, subCategoryId, pathBreadcrumb)
    }
  }, [selectedQuestionnaire, userCompanies])

  useEffect(() => {
    if (!selectedQuestionnaire || !selectedQuestionnaire.classifier || typeDocuments.length === 0) return

    const { typeDocument, typeDocumentId } = selectedQuestionnaire.classifier

    let resolvedTypeDoc = null

    if (typeof typeDocument === 'object' && typeDocument.id) {
      resolvedTypeDoc = typeDocuments.find(td => td.id === typeDocument.id)
    }

    if (!resolvedTypeDoc && typeDocument) {
      resolvedTypeDoc = typeDocuments.find(td => td.name === typeDocument || td.id === typeDocument)
    }

    if (!resolvedTypeDoc && typeDocumentId) {
      resolvedTypeDoc = typeDocuments.find(td => td.id === typeDocumentId)
    }

    if (resolvedTypeDoc) setTypeDocument(resolvedTypeDoc)

  }, [selectedQuestionnaire, typeDocuments])


  //  Guardar selección
  const handleAssignSave = () => {
    if (companyId === NIL_UUID) {
      setShowErrorCompany(true)
      return
    }
    if (!typeDocument) {
      setShowTypeDoc(true)
      return
    }
    if (!select || !Array.isArray(select) || !select[0]) {
      setShowErrorSub(true)
      return
    }

    const selectedNodeId = select[0]
    const selectedNode =
      subCategories.find(cat => String(cat.id) === String(selectedNodeId)) ||
      subCategories.find(cat => String(cat.subcategory?.id) === String(selectedNodeId))


    if (!selectedNode) {
      setShowErrorCategories(true)
      return
    }

    const subCategoryId = selectedNode.subcategory?.id ?? selectedNode.id

    // Construir breadcrumb
    const breadcrumbParts = []
    let currentNode = selectedNode

    while (currentNode) {
      breadcrumbParts.unshift(currentNode.id || currentNode.subcategory?.id)
      if (!currentNode.parentId || currentNode.parentId === 0) break
      currentNode = subCategories.find(cat => String(cat.id) === String(currentNode.parentId))
    }
    const pathBreadcrumb = breadcrumbParts.join('/')
    const categoryNameSelected =
      selectedNode?.subcategory?.name || selectedNode?.name || ''

    const data = {
      solicitudeId: selectedQuestionnaire.id,
      companyId,
      categoryId,
      subCategoryId,
      typeDocument,
      pathBreadcrumb,
      categoryNameSelected,
    }
    handleAssignCategories(data, hideShowDetails)
  }

  const handleBack = () => {
    hideShowDetails()
    setCompanyId(NIL_UUID)
    setCategoryId(0)
    setTypeDocument('')
    setSelect([""])
    setFilteredCategories([])
    setSelectedSubCategoryId(0)
    setExpandedNodes([])
    // Resetear el flag para permitir nueva carga cuando se abra otro cuestionario
    initialLoadDone.current = false
  }

  // Cargar categorías cuando el usuario cambia manualmente la empresa
  useEffect(() => {
    // Solo ejecutar si NO hay cuestionario seleccionado o si el companyId es diferente al del cuestionario
    const questionnaireCompanyId = selectedQuestionnaire?.classifier?.companyId

    // Si el companyId cambió manualmente (no viene del cuestionario)
    if (companyId && companyId !== NIL_UUID && companyId !== questionnaireCompanyId) {
      getUserCategories(companyId, setCategoryId, setSelect)
      setFilteredCategories([])
      setSelectedSubCategoryId(0)
      setExpandedNodes([])
    }
  }, [companyId])

  // ELIMINADO: Este useEffect causaba saltos innecesarios en el árbol
  // La selección ya se maneja en getCategoriesAndSelectSubcategory

  return (
    <div className="questionnairesAssignCategories-container">
      <div className="questionnairesAssignCategories-elements">
        <div className="questionnairesAssignCategories-inputs">
          <div className="questionnaireAssignCategories-subtitle">
            <p className="questionnairesAssignCategories-label-title">{t("digitalContractDashboard.questionnaires.assignCategoryTitle")}</p>
          </div>

          <ClassificationPanelPure
            // Empresa
            companies={userCompanies}
            companyId={companyId}
            onCompanyChange={(e) => handleChange(e, setCompanyId, null, setShowErrorCompany, 'company')}
            companyDisabled={false}
            showCompanyError={showErrorCompany}
            companyErrorMessage={t("questionnairesAssignCategories.company.error")}

            // Categorías y Subcategorías (Syncfusion TreeView)
            categoriesData={filteredCategories}
            categoryId={categoryId}
            onTreeNodeClick={(e) => onChangeTree(e)}
            selectedNodes={[selectedSubCategoryId]}
            expandedNodes={expandedNodes}
            treeRefCallback={(ref) => { treeRef.current = ref }}
            onAddSubcategory={handleAddSubcategory}
            canCreateSubcategory={canCreateSubcategory}

            // Tipo de documento
            typeDocuments={typeDocuments}
            selectedTypeDocument={typeDocument}
            onTypeDocumentChange={(e) => handleChange(e, setTypeDocument, null, setShowTypeDoc)}
            showTypeDocError={showErrorTypeDoc}
            typeDocErrorMessage={t("questionnairesAssignCategories.error.type.document")}

            // Diálogo de crear subcategoría
            showCategoryDialog={showCategoryDialog}
            onCloseCategoryDialog={() => setShowCategoryDialog(false)}
            onSubmitCategory={handleCreateSubcategory}
            categoryName={categoryName}
            onCategoryNameChange={setCategoryName}
            categoryNameError={categoryNameError}
            dialogType="subcategory"

            // Configuración
            usePanelBar={true}
          />

        </div>
        <div className="questionnairesAssignCategories-btns">
          <div className="questionnairesAssignCategories-btn-back">
            <ButtonHLPure
              nameBtn={t("button.return")}
              primary={false}
              onClickBtn={() => handleBack()}
            />
          </div>

          <ButtonHLPure
            nameBtn={t("button.save")}
            primary={true}
            onClickBtn={() => handleAssignSave()}
          />
        </div>
      </div>

      {/* Modales de Loading, Success y Error (IGUAL que signature-profiles) */}
      <DialogSystemGLPure
        showLoadingRequesting={isLoading}
        toggleLoadingRequesting={() => setIsLoading((prev) => !prev)}
        showCompletedRequesting={success}
        toggleCompletedRequesting={() => setSuccess((prev) => !prev)}
        showErrorRequesting={error}
        toggleErrorRequesting={() => setError((prev) => !prev)}
        messageError={textError}
      />
    </div>
  )
}

export { QuestionnnairesAssignCategories as default }